import React, { lazy, Suspense, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useSession } from "./lib/session.tsx";
import { PREVIEWABLE_ROLES } from "./lib/roles.ts";
import { Assistant } from "./components/Assistant.tsx";
import { Wizard, wizardDone } from "./components/Wizard.tsx";
import { Search } from "./components/Search.tsx";
import { DemoExperience } from "./components/DemoTour.tsx";
import { Onboarding } from "./components/Onboarding.tsx";
import { PasswordChange } from "./components/PasswordChange.tsx";
import Login from "./pages/Login.tsx";
import Landing from "./pages/Landing.tsx";
const Dashboard = lazy(() => import("./pages/Dashboard.tsx"));
const Advisor = lazy(() => import("./pages/Advisor.tsx"));
const Memory = lazy(() => import("./pages/Memory.tsx"));
const WarRoom = lazy(() => import("./pages/WarRoom.tsx"));
const Watchdog = lazy(() => import("./pages/Watchdog.tsx"));
const Field = lazy(() => import("./pages/Field.tsx"));
const Calendar = lazy(() => import("./pages/Calendar.tsx"));
const Board = lazy(() => import("./pages/Board.tsx"));
const Projects = lazy(() => import("./pages/Projects.tsx"));
const Settings = lazy(() => import("./pages/Settings.tsx"));
const Guide = lazy(() => import("./pages/Guide.tsx"));
const Console = lazy(() => import("./pages/Console.tsx"));
const Evidence = lazy(() => import("./pages/Evidence.tsx"));
const EvidenceDetail = lazy(() => import("./pages/EvidenceDetail.tsx"));
const Work = lazy(() => import("./pages/Work.tsx"));
const Finance = lazy(() => import("./pages/Finance.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const Filings = lazy(() => import("./pages/Filings.tsx"));
const Contacts = lazy(() => import("./pages/Contacts.tsx"));
const Issues = lazy(() => import("./pages/Issues.tsx"));
const Decisions = lazy(() => import("./pages/Decisions.tsx"));
const Risks = lazy(() => import("./pages/Risks.tsx"));
const Vendors = lazy(() => import("./pages/Vendors.tsx"));
const ClaimsReview = lazy(() => import("./pages/ClaimsReview.tsx"));
const Report = lazy(() => import("./pages/Report.tsx"));
const Hermes = lazy(() => import("./pages/Hermes.tsx"));
const Billing = lazy(() => import("./pages/Billing.tsx"));
const Strategy = lazy(() => import("./pages/Strategy.tsx"));

/**
 * Page-level permission gates — one table drives the route guard. A route
 * absent here is open to every active user (data is still filtered by
 * Security Rules). Any listed permission admits.
 */
const ROUTE_GATES = new Map<string, readonly string[]>([
  ["/strategy", ["strategy.view"]],
  ["/advisor", ["aura.query"]],
  ["/memory", ["aura.query"]],
  ["/war-room", ["aura.query"]],
  ["/watchdog", ["aura.query"]],
  ["/claims-review", ["aura.canonical.approve"]],
  ["/contacts", ["crm.view"]],
  ["/work", ["projects.view"]],
  ["/projects", ["projects.view"]],
  ["/issues", ["projects.view"]],
  ["/decisions", ["projects.view"]],
  ["/risks", ["projects.view"]],
  ["/calendar", ["projects.view"]],
  ["/report", ["projects.view"]],
  ["/hermes", ["hermes.run"]],
  ["/finance", ["finance.view"]],
  ["/vendors", ["finance.view"]],
  ["/filings", ["finance.view"]],
  ["/admin", ["admin.users.manage", "audit.view"]],
  ["/console", ["system.maintenance"]],
]);

function Gate({ path, children }: { path: string; children: React.ReactNode }) {
  const { can, permissions, t } = useSession();
  const needed = ROUTE_GATES.get(path);
  const admitted =
    !needed || needed.some((p) => (p === "system.maintenance" ? permissions.includes(p) : can(p)));
  if (!admitted) return <div className="callout callout-warn">{t("common.noPermission")}</div>;
  return <>{children}</>;
}

function Sidebar({ onOpenSearch }: { onOpenSearch: () => void }) {
  const {
    t,
    profile,
    permissions,
    realPermissions,
    viewAsRole,
    setViewAsRole,
    locale,
    setLocale,
    logout,
    can,
    trialActive,
  } = useSession();
  const canPreview = realPermissions.includes("*");

  return (
    <aside className="sidebar">
      <div>
        <div className="brand-name">{t("app.name")}</div>
        <div className="brand-tagline">{t("app.tagline")}</div>
      </div>

      <button
        type="button"
        className="subtle"
        onClick={onOpenSearch}
        style={{ textAlign: "left", width: "100%" }}
      >
        🔍 {t("search.open")} <span className="faint small mono">Ctrl+K</span>
      </button>

      <nav className="nav">
        <NavLink to="/" end>
          {t("nav.dashboard")}
        </NavLink>
        {can("strategy.view") && <NavLink to="/strategy">{t("nav.strategy")}</NavLink>}
        {can("aura.query") && <NavLink to="/advisor">{t("nav.advisor")}</NavLink>}
        {can("aura.query") && <NavLink to="/memory">{t("nav.memory")}</NavLink>}
        <NavLink to="/evidence">{t("nav.evidence")}</NavLink>
        {can("aura.canonical.approve") && (
          <NavLink to="/claims-review">{t("nav.claimsReview")}</NavLink>
        )}
        {can("aura.query") && <NavLink to="/war-room">{t("nav.warRoom")}</NavLink>}
        {can("aura.query") && <NavLink to="/watchdog">{t("nav.watchdog")}</NavLink>}
        <NavLink to="/field">{t("nav.field")}</NavLink>
        {can("crm.view") && <NavLink to="/contacts">{t("nav.contacts")}</NavLink>}
        {can("projects.view") && <NavLink to="/work">{t("nav.work")}</NavLink>}
        {can("projects.view") && <NavLink to="/projects">{t("nav.projects")}</NavLink>}
        {can("projects.view") && <NavLink to="/issues">{t("nav.issues")}</NavLink>}
        {can("projects.view") && <NavLink to="/decisions">{t("nav.decisions")}</NavLink>}
        {can("projects.view") && <NavLink to="/risks">{t("nav.risks")}</NavLink>}
        {can("projects.view") && <NavLink to="/calendar">{t("nav.calendar")}</NavLink>}
        <NavLink to="/board">{t("nav.board")}</NavLink>
        {can("projects.view") && <NavLink to="/report">{t("nav.report")}</NavLink>}
        {can("hermes.run") && <NavLink to="/hermes">{t("nav.hermes")}</NavLink>}
        {can("finance.view") && <NavLink to="/finance">{t("nav.finance")}</NavLink>}
        {can("finance.view") && <NavLink to="/vendors">{t("nav.vendors")}</NavLink>}
        {can("finance.view") && <NavLink to="/filings">{t("nav.filings")}</NavLink>}
        {(can("admin.users.manage") || can("audit.view")) && (
          <NavLink to="/admin">{t("nav.admin")}</NavLink>
        )}
        {permissions.includes("system.maintenance") && (
          <NavLink to="/console">{t("nav.console")}</NavLink>
        )}
        <NavLink to="/settings">{t("nav.settings")}</NavLink>
        {(trialActive ||
          can("finance.view") ||
          profile?.roles?.includes("super_admin") ||
          profile?.roles?.includes("developer")) && (
          <NavLink to="/billing">{t("nav.billing")}</NavLink>
        )}
      </nav>

      <div className="sidebar-foot">
        <div>
          <div className="who-name">{profile?.displayName}</div>
          <div className="who-role">{profile?.title ?? profile?.roles?.join(", ") ?? ""}</div>
        </div>

        {canPreview && (
          <label className="view-as">
            <span className="faint small">{locale === "es" ? "Ver como" : "View as"}</span>
            <select
              value={viewAsRole ?? ""}
              onChange={(e) => setViewAsRole(e.target.value || null)}
              aria-label={locale === "es" ? "Ver como rol" : "View as role"}
            >
              <option value="">{locale === "es" ? "Mi cuenta (todo)" : "My account (full)"}</option>
              {PREVIEWABLE_ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="lang-toggle" role="group" aria-label={t("nav.language")}>
          <button type="button" aria-pressed={locale === "es"} onClick={() => setLocale("es")}>
            ES
          </button>
          <button type="button" aria-pressed={locale === "en"} onClick={() => setLocale("en")}>
            EN
          </button>
        </div>

        <button type="button" className="subtle" onClick={() => void logout()}>
          {t("nav.signOut")}
        </button>
        {/* "*" is unbounded, so a count would understate it. Both developer
            and super_admin carry the wildcard; developer additionally carries
            the explicit maintenance permission. */}
        <div className="faint small mono">
          {permissions.includes("*") ? t("admin.allPermissions") : `${permissions.length} perms`}
        </div>
      </div>
    </aside>
  );
}

/** Keeps <html lang> in sync so screen readers announce the right language. */
function useDocumentLanguage() {
  const { locale } = useSession();
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
}

export default function App() {
  const {
    firebaseUser,
    loading,
    missingProfile,
    campaignMismatch,
    profile,
    t,
    licenseResolved,
    licensed,
    trial,
    trialResolved,
    trialActive,
    developerBypass,
    demoMode,
  } = useSession();
  const location = useLocation();
  useDocumentLanguage();
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loading) {
    return (
      <div className="auth-screen">
        <div className="muted">
          <span className="spinner" /> {t("common.loading")}
        </div>
      </div>
    );
  }

  if (!firebaseUser) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Signed in to Firebase Auth but with no user record, or a suspended one.
  // Both are dead ends that need an administrator, so say so plainly instead
  // of dropping the person into an app where every query fails.
  if (missingProfile || !profile) {
    return <Login blockedReason="noAccount" />;
  }
  if (campaignMismatch) {
    return <Login blockedReason="wrongCampaign" />;
  }
  if (profile.status !== "active") {
    return <Login blockedReason="suspended" />;
  }
  if (!licenseResolved || !trialResolved) {
    return (
      <div className="auth-screen">
        <div className="muted">
          <span className="spinner" /> {t("common.loading")}
        </div>
      </div>
    );
  }
  if (!licensed && !trialActive && !developerBypass && !demoMode) {
    return <Billing standalone />;
  }
  // A provisioned account's temporary password is replaced before anything else.
  if (profile.mustChangePassword === true && !demoMode) {
    return <PasswordChange />;
  }

  return (
    <div className="shell">
      <Sidebar onOpenSearch={() => setSearchOpen(true)} />
      {searchOpen && <Search onClose={() => setSearchOpen(false)} />}
      <main className="main">
        {trialActive && trial && <TrialBanner />}
        <DemoExperience />
        <Onboarding />
        <PreviewBanner />
        <div key={location.pathname}>
          <Suspense
            fallback={
              <div className="muted">
                <span className="spinner" /> {t("common.loading")}
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route
                path="/strategy"
                element={
                  <Gate path="/strategy">
                    <Strategy />
                  </Gate>
                }
              />
              <Route
                path="/advisor"
                element={
                  <Gate path="/advisor">
                    <Advisor />
                  </Gate>
                }
              />
              <Route
                path="/memory"
                element={
                  <Gate path="/memory">
                    <Memory />
                  </Gate>
                }
              />
              <Route
                path="/war-room"
                element={
                  <Gate path="/war-room">
                    <WarRoom />
                  </Gate>
                }
              />
              <Route
                path="/watchdog"
                element={
                  <Gate path="/watchdog">
                    <Watchdog />
                  </Gate>
                }
              />
              <Route path="/field" element={<Field />} />
              <Route
                path="/calendar"
                element={
                  <Gate path="/calendar">
                    <Calendar />
                  </Gate>
                }
              />
              <Route path="/board" element={<Board />} />
              <Route
                path="/projects"
                element={
                  <Gate path="/projects">
                    <Projects />
                  </Gate>
                }
              />
              <Route path="/evidence" element={<Evidence />} />
              <Route path="/evidence/:evidenceId" element={<EvidenceDetail />} />
              <Route
                path="/claims-review"
                element={
                  <Gate path="/claims-review">
                    <ClaimsReview />
                  </Gate>
                }
              />
              <Route
                path="/contacts"
                element={
                  <Gate path="/contacts">
                    <Contacts />
                  </Gate>
                }
              />
              <Route
                path="/issues"
                element={
                  <Gate path="/issues">
                    <Issues />
                  </Gate>
                }
              />
              <Route
                path="/decisions"
                element={
                  <Gate path="/decisions">
                    <Decisions />
                  </Gate>
                }
              />
              <Route
                path="/risks"
                element={
                  <Gate path="/risks">
                    <Risks />
                  </Gate>
                }
              />
              <Route
                path="/work"
                element={
                  <Gate path="/work">
                    <Work />
                  </Gate>
                }
              />
              <Route
                path="/finance"
                element={
                  <Gate path="/finance">
                    <Finance />
                  </Gate>
                }
              />
              <Route
                path="/vendors"
                element={
                  <Gate path="/vendors">
                    <Vendors />
                  </Gate>
                }
              />
              <Route
                path="/filings"
                element={
                  <Gate path="/filings">
                    <Filings />
                  </Gate>
                }
              />
              <Route
                path="/report"
                element={
                  <Gate path="/report">
                    <Report />
                  </Gate>
                }
              />
              <Route
                path="/hermes"
                element={
                  <Gate path="/hermes">
                    <Hermes />
                  </Gate>
                }
              />
              <Route
                path="/admin"
                element={
                  <Gate path="/admin">
                    <Admin />
                  </Gate>
                }
              />
              <Route path="/settings" element={<Settings />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/guide" element={<Guide />} />
              <Route
                path="/console"
                element={
                  <Gate path="/console">
                    <Console />
                  </Gate>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </div>
        <SiteFooter />
      </main>
      <Assistant />
      <FirstRunWizard />
    </div>
  );
}

/** Sticky banner shown while a privileged user previews another role's view. */
function PreviewBanner() {
  const { viewAsRole, setViewAsRole, locale } = useSession();
  if (!viewAsRole) return null;
  return (
    <div className="preview-banner" role="status">
      <span>
        {locale === "es" ? "Viendo como" : "Viewing as"} <strong>{viewAsRole}</strong>
        {locale === "es"
          ? " — vista previa de solo lectura de lo que ve este rol."
          : " — read-only preview of what this role sees."}
      </span>
      <button type="button" className="subtle" onClick={() => setViewAsRole(null)}>
        {locale === "es" ? "Salir de la vista previa" : "Exit preview"}
      </button>
    </div>
  );
}

/** Opens the setup tour on first login, and whenever Settings requests it. */
function FirstRunWizard() {
  const { demoMode, firebaseUser, profile } = useSession();
  const [open, setOpen] = useState(
    () => !wizardDone(firebaseUser?.uid) && profile?.welcomePending === undefined,
  );
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-wizard", handler);
    return () => window.removeEventListener("open-wizard", handler);
  }, []);
  if (demoMode) return null;
  return open ? <Wizard onClose={() => setOpen(false)} /> : null;
}

function TrialBanner() {
  const { trial, locale } = useSession();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  if (!trial) return null;
  const remainingMs = Math.max(0, trial.endsAt.toMillis() - now);
  const hours = Math.floor(remainingMs / 3_600_000);
  const minutes = Math.floor((remainingMs % 3_600_000) / 60_000);
  const remainingUsd = Math.max(
    0,
    (trial.spendLimitMicros - trial.spentMicros - trial.reservedMicros) / 1_000_000,
  );
  return (
    <div className="preview-banner" role="status">
      <span>
        <strong>{locale === "es" ? "Prueba completa" : "Full-access trial"}</strong>
        {locale === "es"
          ? ` · ${hours}h ${minutes}m restantes · $${remainingUsd.toFixed(2)} de IA disponibles`
          : ` · ${hours}h ${minutes}m remaining · $${remainingUsd.toFixed(2)} AI budget available`}
      </span>
      <NavLink to="/billing">
        {locale === "es" ? "Activar suscripción" : "Activate subscription"}
      </NavLink>
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="app-footer">
      Built by{" "}
      <a href="https://elevateelpaso.com" target="_blank" rel="noreferrer noopener">
        Elevateelpaso.com
      </a>{" "}
      · Powered by Passion for Life
    </footer>
  );
}
